import useLabels from "../../hooks/useLabels";
import SecondaryBar from "../SecondaryBar";
import {
  BottlingColumn,
  CloseColumn,
  CTA,
  InfoCard,
  TankColumn,
} from "../Generic";
import {
  selectActiveWinery,
  selectAnalysis,
  selectConfigs,
  selectDrugs,
  selectTanks,
  selectTreatments,
  selectUsers,
} from "../../context/selectors";
import {
  getDrugs,
  getProtocols,
  getReportByType,
  getTanks,
  getUsers,
} from "../../services/utils";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ReactDatePicker from "react-datepicker";
import { ROLES } from "../../constants/base";
import useAuth from "../../hooks/useAuth";
import tank from "../../assets/icons/tank.png";
import treatment from "../../assets/icons/treatment.png";
import ReportCard from "./ReportCard";
import { setTanks } from "../../context/tanks/tanksSlice";
import { Select } from "../FormComponents";
import { useForm, useWatch } from "react-hook-form";
import { setDrugs } from "../../context/substances/drugsSlice";
import { createColumnHelper } from "@tanstack/react-table";
import { computeOperationRow } from "../../services/common";
import { addDays, format, subYears } from "date-fns";
import TableCell from "../TableEdit/TableCell";
import TableEdit from "../TableEdit";
import { SkeletonTableEdit } from "../Skeleton/Skeleton";
import { setUsers } from "../../context/users/usersSlice";
import { setAnalysis } from "../../context/protocols/analysisSlice";
import { setTreatments } from "../../context/protocols/treatmentsSlice";
import { protocol } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../components/Icon";

const Report = ({ isMobile }) => {
  const [getLabel] = useLabels();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const activeWinery = useSelector(selectActiveWinery);
  const configs = useSelector(selectConfigs);
  const tanks = useSelector(selectTanks);
  const drugs = useSelector(selectDrugs);
  const users = useSelector(selectUsers);
  const analysis = useSelector(selectAnalysis);
  const treatments = useSelector(selectTreatments);
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();

  const [isSearchDisabled, setIsSearchDisabled] = useState(true);
  const [costResults, setCostResults] = useState([]);
  const [drugResults, setDrugResults] = useState([]);
  const [tankResults, setTankResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const today = new Date();
  const [dates, setDates] = useState({
    startDate: new Date(subYears(today, 1).setHours(0, 0, 0)),
    endDate: new Date(today.setHours(23, 59, 59)),
    minDate: null,
  });

  const {
    reset,
    register,
    formState: { errors, isValid },
    setError,
    setValue,
    clearErrors,
    getValues,
    control,
  } = useForm({
    defaultValues: {
      drug_id: { label: "", value: "" },
      tank_id: { label: "", value: "" },
    },
    mode: "onTouched",
  });

  const { drug_batch, drug_id, tank_id } = useWatch({ control });

  const columnHelper = createColumnHelper();

  const drugColumns = useMemo(() => {
    return [
      columnHelper.accessor(
        (row) =>
          computeOperationRow(
            {
              ...row,
              id: row?.operation_id,
              type: row?.operation_type,
              protocol_id: row?.treatments?.[0]?.protocol_id,
            },
            treatments,
            analysis,
            getLabel
          ),
        {
          header: getLabel("operationType"),
          cell: TableCell,
          meta: {
            type: "operationType",
            filterVariant: "select",
            selector: "operation_type",
            additional: "opWithStart",
          },
        }
      ),
      columnHelper.accessor("operationSrcTank", {
        header: getLabel("operationSrcTank"),
        cell: (props) =>
          props.row?.original?.type === "NEW_LOT" ? (
            props.row?.original?.src_tanks?.[0]?.name || ""
          ) : (
            <TankColumn
              rowData={props.row?.original?.src_tanks}
              tanks={tanks}
            />
          ),
        meta: {
          array: "src_tanks",
          selector: "name",
          filterVariant: "text",
        },
        filterFn: "MultipleRowsFilterFn",
      }),
      columnHelper.accessor("operationDestTank", {
        header: getLabel("operationDestTank"),
        cell: (props) =>
          props.row?.original?.type === "CLOSING" ? (
            <CloseColumn rowData={props?.row?.original?.dest_tanks} />
          ) : props.row?.original?.type === "BOTTLING" ? (
            <BottlingColumn rowData={props?.row?.original?.dest_tanks} />
          ) : (
            <TankColumn
              rowData={props.row?.original?.dest_tanks}
              tanks={tanks}
            />
          ),
        meta: {
          array: "dest_tanks",
          selector: "name",
          filterVariant: "text",
        },
        filterFn: "MultipleRowsFilterFn",
      }),
      columnHelper.accessor(
        (row) =>
          row?.expire_date
            ? format(new Date(row.completion_date), configs.dateFormat)
            : "",
        {
          // TO DO short date se repetitive nell stesso giorno
          header: getLabel("operationCompletionDate"),
          cell: TableCell,
          meta: {
            isFilterDisabled: true,
          },
        }
      ),
      columnHelper.accessor(
        (row) =>
          row?.completed_by
            ? users?.find((u) => u?.id === row?.completed_by)?.username
            : "",
        {
          header: getLabel("operationCompletionUser"),
          cell: TableCell,
          meta: {
            array: users,
            selector: "completed_by",
            filterVariant: "text",
          },
        }
      ),
      columnHelper.accessor(
        (row) =>
          `${row?.drug_batch_cost || ""} ${row?.drug_batch_cost ? "€" : ""}`,
        {
          header: getLabel((drug_batch?.value === "ALL" || !drug_batch?.value) ? "drugTotalCost" : "drugCost"),
          cell: TableCell,
          meta: {
            isFilterDisabled: true,
          },
        }
      ),
    ];
  });

  const loadTanks = async () => {
    const currentTanks = await getTanks(activeWinery?.id, axiosPrivate);
    dispatch(setTanks(currentTanks));
  };

  const loadDrugs = async () => {
    const currentDrugs = await getDrugs(axiosPrivate);
    dispatch(setDrugs(currentDrugs));
  };

  const loadUsers = async () => {
    const currentUsers = await getUsers(axiosPrivate);
    dispatch(setUsers(currentUsers));
  };

  const loadAnalysis = async () => {
    const currentAnalysis = await getProtocols("analysis", axiosPrivate);
    dispatch(setAnalysis(currentAnalysis));
  };

  const loadTreatments = async () => {
    const currentTreatments = await getProtocols("treatment", axiosPrivate);
    dispatch(setTreatments(currentTreatments));
  };

  useEffect(() => {
    if (!tanks || tanks.length === 0) {
      loadTanks();
    }
    if (!drugs || drugs.length === 0) {
      loadDrugs();
    }
    if (!users || users.length === 0) {
      loadUsers();
    }
    if (!analysis || analysis.length === 0) {
      loadAnalysis();
    }
    if (!treatments || treatments.length === 0) {
      loadTreatments();
    }
  }, []);

  useEffect(() => {
    const TYPES = ["DRUG", "DRUG_BATCH", "COST", "TANK"];

    TYPES.forEach((type) => {
      const key = `report_${type.toLowerCase()}_state`;
      const savedState = localStorage.getItem(key);

      if (!savedState) return;

      const { filters, results } = JSON.parse(savedState);

      // restore form values
      Object.entries(filters || {}).forEach(([field, value]) => {
        setValue(field, value);
      });

      // restore table data
      if (["DRUG", "DRUG_BATCH"].includes(type)) {
        setDrugResults(results || []);
      } else if (type === "TANK") {
        setTankResults(results || []);
      } else if (type === "COST") {
        setCostResults(results || []);
      }

      localStorage.removeItem(key);
    });
  }, []);

  const isInconsistentSelection = useMemo(() => {
    if (!drug_id?.value) return false;
    if (!drug_batch?.value) return false;
    if (drug_batch.value === "ALL") return false;

    return !drug_id.batches?.some((b) => b?.drug_batch_id === drug_batch.value);
  }, [drug_id, drug_batch]);

  useEffect(() => {
    setIsSearchDisabled(isDrugSearchDisabled());

    if (isInconsistentSelection) {
      setValue("drug_batch", null);
      setDrugResults([]); // reset SOLO se incoerente
    }
  }, [isInconsistentSelection, drug_id, drug_batch]);

  const resetDrugResults = useCallback(() => {
    setDrugResults([]);
  }, []);

  useEffect(() => {
    resetDrugResults();
  }, [
    drug_id?.value,
    drug_batch?.value,
    dates?.startDate,
    dates?.endDate,
  ]);


  const isDrugSearchDisabled = () => {
    if (!drug_id?.value) return true;

    // se ha batch disponibili, devo scegliere ALL o uno specifico
    if (drug_id?.batches?.length > 0 && !drug_batch?.value) {
      return true;
    }

    return false;
  };

  const shouldShowInterval = (type) => {
    switch (type) {
      case "DRUG":
      case "TANK":
      case "COST":
        return true;
      default:
        return false;
    }
  };

  const normalizeQuantity = (quantity, unit_id) => {
    // unit_id:
    // 1 = mg/L
    // 2 = g/hL
    // 6,7 = g, mg (assumiamo peso)
    // tutto convertito in Kg o L

    if ([1].includes(unit_id)) {
      // mg → Kg
      return quantity / 1_000_000;
    }

    if ([2, 6, 7].includes(unit_id)) {
      // g → Kg
      return quantity / 1_000;
    }

    if (unit_id === 3) {
      // L
      return quantity;
    }

    return quantity;
  };

  const normalizeQuantityForCost = (qty, unit_id) => {
    // g → g
    if ([2, 6, 7].includes(unit_id)) {
      return qty * 1000; // Kg → g
    }

    // L → L
    if (unit_id === 3) {
      return qty;
    }

    return qty;
  };

  const calculateOperationCost = (operation, type, selectedBatchId) => {
    let total = 0;

    operation.treatments?.forEach((t) => {
      t.drug_batches?.forEach((b) => {
        if (type === "DRUG_BATCH" && b.drug_batch_id !== selectedBatchId) return;

        const normalizedQty = normalizeQuantityForCost(
          b.quantity,
          t.unit_id
        );

        total += normalizedQty * b.unitary_cost;
      });
    });

    return Number(total.toFixed(6));
  };

  const renderHeader = () => {
    return (
      <div className="primary_container_menu">
        <h2>{getLabel("report")}</h2>
      </div>
    );
  };

  const renderInterval = () => {
    return (
      <div className="interval_wrapper">
        <div className="interval_row">
          <div className="interval_value">
            <span>{getLabel("dateFrom")}</span>
            <ReactDatePicker
              selected={dates.startDate}
              onChange={(date) => {
                const newStart = new Date(date);
                newStart.setHours(0, 0, 0, 0); // imposta anche millisecondi per sicurezza
                setDates({
                  ...dates,
                  startDate: newStart,
                });
              }}
              selectsStart
              startDate={dates.startDate}
              endDate={dates.endDate}
              dateFormat={configs.shortDateFormat}
              maxDate={new Date()}
            />
            <span>{getLabel("dateTo")}</span>
            <ReactDatePicker
              selected={dates.endDate}
              onChange={(date) => {
                const newEnd = new Date(date);
                newEnd.setHours(23, 59, 59, 999); // imposta anche millisecondi per sicurezza
                setDates({
                  ...dates,
                  endDate: newEnd,
                });
              }}
              selectsEnd
              startDate={dates.startDate}
              endDate={dates.endDate}
              minDate={dates.startDate}
              dateFormat={configs.shortDateFormat}
              maxDate={new Date()}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderCTA = (type) => {
    return (
      <CTA onClick={() => fetchDataByType(type)} disabled={isSearchDisabled}>
        {getLabel("reportStartSearch")} <Icon icon="fa-search" />
      </CTA>
    );
  };

  const fetchDataByType = async (type) => {
    if (!["DRUG", "DRUG_BATCH"].includes(type)) return;
    if (isDrugSearchDisabled()) return;

    const isAllBatches = drug_batch?.value === "ALL" || !drug_batch?.value;
    const params = {
      drug_id: drug_id.value,
      drug_batch_id: isAllBatches ? null : drug_batch.value,
      // startDate: getUTCStartOfDay(dates.startDate),
      // endDate: getUTCEndOfDay(dates.endDate),

      startDate: dates.startDate.getTime(),
      endDate: dates.endDate.getTime(),
    };

    try {
      setIsLoading(true);

      const res = await getReportByType(
        isAllBatches ? "DRUG" : "DRUG_BATCH",
        params,
        axiosPrivate
      );
      console.log('res', res)
      if (Array.isArray(res)) {
        const enriched = res.map((operation) => {
          const cost = calculateOperationCost(
            operation,
            type,
            drug_batch?.value
          );

          return {
            ...operation,
            drug_batch_cost: Number(cost.toFixed(2)),
          };
        });
        
        setDrugResults(enriched);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderTanksReport = () => {
    return (
      <div className="card_wrapper">
        <h6 className="card_title">{getLabel("reportTanks")}</h6>
        <div className="card">
          <div className="card_row">
            <ReportCard img={tank} alt_img={""} isMobile={isMobile}>
              <Select
                control={control}
                name="tank_id"
                isDisabled={tanks?.filter((t) => t.quantity > 0)?.length === 0}
                label={getLabel("operationDestTankName")}
                placeholder={getLabel("operationTransferTankPlaceholder")}
                // required={getLabel("inputRequiredError")}
                options={tanks
                  ?.filter((t) => t.quantity > 0)
                  ?.map?.((tank) => {
                    return {
                      value: tank.id,
                      label: tank.name,
                      name:
                        tank?.name +
                        ` (${tank?.batch_name || getLabel("tankState_EMPTY")})`,
                    };
                  })}
              />
              {tank_id?.value && (
                <div>
                  <Select
                    control={control}
                    name="tank_select"
                    isDisabled={
                      tanks?.filter((t) => t.quantity > 0)?.length === 0
                    }
                    label={getLabel("operationDestTankName")}
                    placeholder={getLabel("operationTransferTankPlaceholder")}
                    // required={getLabel("inputRequiredError")}
                    options={[
                      {
                        value: "",
                        label: "",
                      },
                      {
                        value: "",
                        label: "costi",
                      },
                      {
                        value: "",
                        label: "prodotti",
                      },
                    ]}
                  />
                </div>
              )}
            </ReportCard>
          </div>
        </div>
      </div>
    );
  };

  const renderCostsReport = () => {
    return (
      <div className="card_wrapper">
        <h6 className="card_title">{getLabel("reportCosts")}</h6>
        <div className="card">
          <div className="card_row">
            <ReportCard img={tank} alt_img={""} isMobile={isMobile}>
              <Select
                control={control}
                name="tank_id"
                isDisabled={tanks?.filter((t) => t.quantity > 0)?.length === 0}
                label={getLabel("operationDestTankName")}
                placeholder={getLabel("operationTransferTankPlaceholder")}
                // required={getLabel("inputRequiredError")}
                options={tanks
                  ?.filter((t) => t.quantity > 0)
                  ?.map?.((tank) => {
                    return {
                      value: tank.id,
                      label: tank.name,
                      name:
                        tank?.name +
                        ` (${tank?.batch_name || getLabel("tankState_EMPTY")})`,
                    };
                  })}
              />
              {tank_id?.value && (
                <div>
                  <Select
                    control={control}
                    name="tank_select"
                    isDisabled={
                      tanks?.filter((t) => t.quantity > 0)?.length === 0
                    }
                    label={getLabel("operationDestTankName")}
                    placeholder={getLabel("operationTransferTankPlaceholder")}
                    // required={getLabel("inputRequiredError")}
                    options={[
                      {
                        value: "",
                        label: "",
                      },
                      {
                        value: "",
                        label: "costi",
                      },
                      {
                        value: "",
                        label: "prodotti",
                      },
                    ]}
                  />
                </div>
              )}
            </ReportCard>
          </div>
        </div>
      </div>
    );
  };

  const handleRowClick = (row, type) => {
    if (!type) return;

    // salva dinamicamente lo stato nel localStorage
    const stateToSave = {
      filters: {},
      results: [],
    };

    switch (type) {
      case "DRUG":
      case "DRUG_BATCH":
        stateToSave.filters = {
          drug_id: getValues("drug_id"),
          drug_batch: getValues("drug_batch"),
          dates,
        };
        stateToSave.results = drugResults;
        break;

      case "TANK":
        stateToSave.filters = {
          tank_id: getValues("tank_id"),
          dates,
        };
        stateToSave.results = tankResults;
        break;

      case "COST":
        stateToSave.filters = {
          cost_type: getValues("cost_type"),
          dates,
        };
        stateToSave.results = costResults;
        break;

      default:
        break;
    }

    // key dinamica per tipo report
    const key = `report_${type.toLowerCase()}_state`;
    localStorage.setItem(key, JSON.stringify(stateToSave));

    navigate(
      `/programming/history/${row.original?.operation_type?.toLowerCase?.()}/${
        row.original?.operation_id
      }`
    );
  };

  const renderTreatmentsReport = () => {
    const ctaType =
      drug_batch?.value && drug_batch.value !== "ALL" ? "DRUG_BATCH" : "DRUG";

    return (
      <div className="card_wrapper">
        <h6 className="card_title">{getLabel("reportTreatments")}</h6>
        <div className="card">
          <div className="card_row">
            <ReportCard img={treatment} alt_img={""} isMobile={isMobile}>
              <Select
                control={control}
                name="drug_id"
                isDisabled={drugs?.length === 0}
                label={getLabel("substanceTreatment")}
                placeholder={getLabel("protocolProductPlaceholder")}
                isSearchable={true}
                // required={getLabel("inputRequiredError")}
                options={drugs?.map?.((drug) => {
                  return {
                    value: drug.id,
                    label: drug.name,
                    name:
                      drug?.name +
                      (drug?.type
                        ? ` (${getLabel(
                            `drugType_${drug?.type}`
                          )?.toLowerCase?.()})`
                        : ""),
                    batches: drug?.batches,
                  };
                })}
              />
              {drug_id?.value && drug_id?.batches?.length > 0 && (
                <div>
                  <Select
                    control={control}
                    name="drug_batch"
                    // isDisabled={drugs?.filter((t) => t.quantity > 0)?.length === 0}
                    label={getLabel("drugBatchNumber")}
                    placeholder={getLabel("drugBatchNumbersPlaceholder")}
                    // required={getLabel("inputRequiredError")}
                    options={[
                      {
                        value: "ALL",
                        label: getLabel("reportAllDrugBatches"),
                      },
                      ...(drug_id?.batches?.map((b) => {
                        return {
                          value: b?.drug_batch_id,
                          label: b?.batch_number,
                        };
                      }) || []),
                    ]}
                  />
                </div>
              )}
              {shouldShowInterval && renderInterval()}
              {renderCTA(ctaType)}
            </ReportCard>
            {drugResults?.length > 0 && (
              <Suspense fallback={<SkeletonTableEdit colCount={6} />}>
                <TableEdit
                  columns={drugColumns}
                  defaultData={drugResults}
                  type={"treatments"}
                  filterType={"treatments"}
                  isMobile={isMobile}
                  rowClickHandler={(row) =>
                    handleRowClick(
                      row,
                      drug_batch?.value && drug_batch.value !== "ALL"
                        ? "DRUG_BATCH"
                        : "DRUG"
                    )
                  }
                  isLegend={false}
                />
              </Suspense>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="primary_container">
      <SecondaryBar
        breadCrumb={[getLabel("reportNavLink")]}
        isBasePathNeeded={true}
      />
      {renderHeader()}
      {renderTanksReport()}
      {renderCostsReport()}
      {renderTreatmentsReport()}
    </div>
  );
};

export default Report;
